import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../api/client.js';
import { formatNumber, intlLocale } from '../i18n.js';
import { MIB, useLocale } from './site.js';

export type Range = readonly number[];
export const lo = (r: Range) => r[0] ?? 0;
export const hi = (r: Range) => r[1] ?? 0;

export function useModernization(siteId: string) {
  const locale = useLocale();
  return useQuery({
    queryKey: ['site', siteId, 'modernization', locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/modernization', {
          params: { path: { siteId }, query: { locale } },
        }),
      ),
  });
}

export type Modernization = NonNullable<ReturnType<typeof useModernization>['data']>;
export type ModernPackage = Modernization['packages'][number];

export function useFormat() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const money = (v: number) =>
    new Intl.NumberFormat(intlLocale(lang), {
      style: 'currency',
      currency: 'EUR',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
      maximumFractionDigits: Number.isInteger(v) ? 0 : 2,
    });
  const euro = (v: number) => money(v).format(v);
  const num = (v: number, digits = 1) => formatNumber(v, lang, digits);
  /** A range of plain numbers, one number when both ends are the same: "3–3,5". */
  const span = (r: Range) => (lo(r) === hi(r) ? num(hi(r)) : `${num(lo(r))}–${num(hi(r))}`);
  const hours = (r: Range) => t('modern.hours', { value: span(r) });
  // "275–316 €" as the audit writes it (decision 7, 12.10), one sign for both ends; browsers
  // without formatRange (Firefox before 116) get the sign twice rather than an error (review #184).
  const euroRange = (r: Range) => {
    if (lo(r) === hi(r)) return euro(hi(r));
    const nf = money(hi(r));
    return typeof nf.formatRange === 'function'
      ? nf.formatRange(lo(r), hi(r))
      : `${euro(lo(r))}–${euro(hi(r))}`;
  };
  return {
    euro,
    euroRange,
    span,
    hours,
    /** Effort and cost in one: "3–3,5 h · 255–300 €". */
    effort: (h: Range, cost: Range) => `${hours(h)} · ${euroRange(cost)}`,
    // Seconds to the hundredth, as on the speed page: a server answer of 6 ms is not "0 s".
    seconds: (ms: number) => t('modern.seconds', { value: num(ms / 1000, 2) }),
    megabytes: (bytes: number) => t('modern.megabytes', { value: num(bytes / MIB) }),
    percent: (share: Range) =>
      lo(share) === hi(share)
        ? `${num(hi(share) * 100, 0)} %`
        : `${num(lo(share) * 100, 0)}–${num(hi(share) * 100, 0)} %`,
    months: (r: Range) =>
      hi(r) < 1
        ? t('modern.paybackUnderMonth')
        : t('modern.paybackMonths', { from: num(Math.max(lo(r), 1), 0), to: num(hi(r), 0) }),
    /** One payback time: "unter 1 Monat" or "≈ 3 Monate". */
    month: (m: number) =>
      m < 1 ? t('modern.paybackUnderMonth') : t('modern.aboutMonths', { count: Math.round(m) }),
    num,
  };
}

/** The packages counted in money by the time they win on the home page. */
const BY_SPEED: ReadonlySet<string> = new Set(['quick_start', 'relaunch']);

/**
 * Why a package counted by speed wins no money (SP-A2.71): the home page is not measured yet, or
 * it loads fast already (up to 2.5 s, as the API reckons) – no time to win, whatever the figures.
 */
export const noSpeedGain = (p: ModernPackage, lcpMs: number | null) =>
  BY_SPEED.has(p.key) && !p.uplift ? (lcpMs === null ? 'notMeasured' : 'fast') : null;

/** When the package pays off, or why it is not counted in money; `lcpMs` of the home page. */
export function usePayback() {
  const { t } = useTranslation();
  const f = useFormat();
  return (p: ModernPackage, lcpMs: number | null) => {
    if (p.paybackMonths) return f.months(p.paybackMonths);
    const still = noSpeedGain(p, lcpMs);
    if (still) return t(still === 'fast' ? 'modern.payback.fast' : 'modern.payback.notMeasured');
    // Figures given, but the cautious gain rounds to nothing.
    if (p.uplift)
      return t(p.monthlyGain ? 'modern.payback.tooSmallShort' : 'modern.payback.needFigures');
    return t(`modern.packages.${p.key}.paybackNote`);
  };
}

/**
 * All packages classic and with AI, and what AI saves (decision 2 of SP-UX10.4): the ranges of
 * the packages added up without the relaunch, as the API adds them – a relaunch replaces the
 * other packages. A relaunch alone is all there is. The share is the API's for the same packages
 * (`modernizationPlan`: 1 − hours with AI / classic hours, upper ends); the API's `aiSaving` also
 * holds the relaunch's own share, which would not match the sums beside it (review #184).
 */
export function savingsOf(packages: readonly ModernPackage[]) {
  const others = packages.filter((p) => p.key !== 'relaunch');
  const counted = others.length > 0 ? others : packages;
  const sum = (pick: (p: ModernPackage) => Range) =>
    [
      counted.reduce((s, p) => s + lo(pick(p)), 0),
      counted.reduce((s, p) => s + hi(pick(p)), 0),
    ] as const;
  const classic = { hours: sum((p) => p.hours.classic), cost: sum((p) => p.cost.classic) };
  const ai = { hours: sum((p) => p.hours.ai), cost: sum((p) => p.cost.ai) };
  const saved = [lo(classic.cost) - lo(ai.cost), hi(classic.cost) - hi(ai.cost)].sort(
    (a, b) => a - b,
  );
  const share =
    hi(classic.hours) > 0 ? Math.round((1 - hi(ai.hours) / hi(classic.hours)) * 100) / 100 : 0;
  return { classic, ai, saved, share, withoutRelaunch: counted.length < packages.length };
}

export type Quarter = 'quick' | 'big' | 'later' | 'skip';
/** Cost with AI that splits cheap from expensive: the middle of the old chart, 100 € … 20 000 €. */
const SPLIT_EUR = Math.sqrt(100 * 20_000);
/** Where a package sits in effort and benefit: much benefit for little money is a quick win. */
export function quarterOf(p: ModernPackage): Quarter {
  const cheap = hi(p.cost.ai) < SPLIT_EUR;
  if (p.benefit >= 50) return cheap ? 'quick' : 'big';
  return cheap ? 'later' : 'skip';
}

/** Where the lines and their names sit in the payback chart (viewBox 600 × 146). */
const ROI = { top: 14, bottom: 128, left: 8, right: 390, height: 146, gap: 14 };

/**
 * The money over 12 months (site-modernization.html .roi): the cost with AI first, then the
 * monthly gain, optimistic and cautious. Drawn to scale; each line named right of its end, so a
 * name never sits on a line, and the two names kept apart when the ends are close. Colours from
 * the theme.
 */
export function PaybackChart({
  cost,
  gain,
  payback,
}: {
  cost: number;
  gain: Range;
  payback: Range;
}) {
  const { t } = useTranslation();
  const f = useFormat();
  const months = 12;
  const { top, bottom, left, right, height, gap } = ROI;
  const endHigh = -cost + hi(gain) * months;
  const endLow = -cost + lo(gain) * months;
  // The gain is never negative: the lines start at the lowest point, the cost.
  const maxV = Math.max(endHigh, 0);
  const x = (m: number) => left + (m / months) * (right - left);
  const y = (v: number) => top + ((maxV - v) / (maxV + cost || 1)) * (bottom - top);
  let optY = y(endHigh) + 4;
  let cauY = y(endLow) + 4;
  if (cauY - optY < gap) {
    const mid = (optY + cauY) / 2;
    optY = mid - gap / 2;
    cauY = mid + gap / 2;
  }
  // Both inside the drawing.
  const shift = Math.max(0, 12 - optY) - Math.max(0, cauY - (height - 4));
  optY += shift;
  cauY += shift;
  return (
    <svg
      viewBox={`0 0 600 ${height}`}
      role="img"
      aria-label={t('modern.payback.description', {
        cost: f.euro(cost),
        low: f.euro(endLow),
        high: f.euro(endHigh),
      })}
    >
      <line className="zero" x1={left} x2={right} y1={y(0)} y2={y(0)} />
      <text x={left} y={y(0) - 5}>
        {f.euro(0)}
      </text>
      <text x={left} y={bottom + 14}>
        −{f.euro(cost)}
      </text>
      <path className="opt" d={`M${x(0)} ${y(-cost)} L${x(months)} ${y(endHigh)}`} />
      <path className="cau" d={`M${x(0)} ${y(-cost)} L${x(months)} ${y(endLow)}`} />
      <text className="opt" x={right + 8} y={optY}>
        {t('modern.payback.optimistic', { value: f.month(lo(payback)) })}
      </text>
      <text className="cau" x={right + 8} y={cauY}>
        {t('modern.payback.cautious', { value: f.month(hi(payback)) })}
      </text>
    </svg>
  );
}
